import {AppBar, Box, Grid, IconButton, Typography} from '@material-ui/core';
import {AppBarStyles} from 'assets/tss/commons';
import {
    COMPLEMENT_MODULES,
    Constants,
    HistoryRoutes,
    INITIAL_MODULES,
    Messages,
    MODULES,
    Roles,
    SnackbarVariants,
    TABLE_PER_PAGE,
    USER_DATA,
} from 'commons/constants';
import {
    ClientContactServiceRequest,
    EfeResponse,
    ErrorResponse,
    ExpedientResponse,
    Layout,
    RegisterServiceValues,
} from 'commons/models';
import {TableAction} from 'commons/models/components/EfeTableValues';
import {ClientResponse, GeolocationInfo} from 'commons/types/Types';
import {getGeolocation, modulesValidation} from 'commons/utils/Functions';
import EfeTable from 'components/efe-table/EfeTable';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {useUserContext} from 'context/user/UserContext';
import {useSpinner} from 'hooks';
import {BottomContent, MainContent} from 'layouts/commons';
import {ADMIN_USERS} from 'modules/dashboard/dashboard-users/UsersTable';
import {useSnackbar} from 'notistack';
import React from 'react';
import {useHistory} from 'react-router-dom';
import {ClientService, KeycloakService, LayoutServices} from 'services';
import Search from '@material-ui/icons/Search';
import {EfeFieldText, EfeModal} from 'components';
import {functions} from 'commons/utils';
import EfeLocationRequest from 'components/efe-location-request/EfeLocationRequest';

function ClientStatus() {
    const history = useHistory();
    const {
        updateMenu,
        setExpedientId,
        setFatherKycId,
        setPersonType,
        updateLayout,
        setKycId,
        setForeign,
        geolocationData,
        expedientId,
        setGeolocationData,
    } = useMenuContext();
    const {user, updateUser} = useUserContext();
    const classes = AppBarStyles();
    const {enqueueSnackbar} = useSnackbar();

    const [page, setPage] = React.useState<number>(1);
    const [users, setUsers] = React.useState<ClientResponse>();
    const [showView, setShowView] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [open, setOpen] = React.useState<boolean>(false);
    const [filterValue, setFilterValue] = React.useState<string>('');
    const [rowsPerPage, setRowsPerPage] = React.useState<number>(TABLE_PER_PAGE);
    const [clientDataValue, setClientDataValue] = React.useState<ClientContactServiceRequest>();

    async function validateLocation(expedientId: number, clientData: ClientContactServiceRequest) {
        if (!geolocationData.latitude || null === geolocationData.latitude) {
            setOpen(!open);
        } else {
            const isValid = await saveGeolocation(expedientId, geolocationData);
            if (isValid) {
                await loadLayout(clientData);
            }
        }
    }

    async function saveGeolocation(expedientId: number, geolocationInfo: GeolocationInfo): Promise<boolean> {
        if (geolocationInfo.latitude && null != geolocationInfo.latitude) {
            try {
                setIsLoading(true);
                const response = await ClientService.saveGeolocationData(
                    expedientId,
                    geolocationInfo
                );
                if (response.data && response.data.message) {
                    setGeolocationData(geolocationInfo);
                } else {
                    enqueueSnackbar(Messages.SAVE_GEOLOCATION_ERROR, {
                        variant: SnackbarVariants.ERROR,
                    });
                }
                setIsLoading(false);
                return true;
            } catch (error: ErrorResponse | any) {
                console.log(error);
                const message = error.data && error.data.errors ? error.data.errors[0] : '';
                await handleRejectShareLocation(true, message || Messages.SAVE_GEOLOCATION_ERROR);
                setIsLoading(false);
                return false;
            }
        }
        return true;
    }

    async function getLayoutData(params: RegisterServiceValues) {
        const layoutResponse: EfeResponse<Layout> = await LayoutServices.getLayoutByPersonType(
            params
        );
        if (
            layoutResponse &&
            layoutResponse.data &&
            layoutResponse.data.views &&
            layoutResponse.data.views.length
        ) {
            updateLayout(layoutResponse.data);
            updateMenu([
                ...modulesValidation([...MODULES], layoutResponse.data),
                ...COMPLEMENT_MODULES,
            ]);
        } else {
            updateMenu([...INITIAL_MODULES]);
        }
    }

    async function getExpedientStatus(clientData: ClientContactServiceRequest) {
        try {
            const response: EfeResponse<ExpedientResponse> = await ClientService.getExpedientStatus(
                clientData.expedientId
            );
            if (response && response.data) {
                const data: ExpedientResponse = response.data;
                if (
                    Constants.AML_AUTHORIZED_ID !== data.stateKey &&
                    Constants.AML_ONBOARDING_ID !== data.stateKey
                ) {
                    setShowView(true);
                } else {
                    setExpedientId(clientData.expedientId);
                    setFatherKycId(clientData.kycId ? clientData.kycId : 0);
                    setPersonType(clientData.personTypeKey);
                    setForeign(!!clientData.foreign);
                    setKycId(-1);
                    setClientDataValue(clientData);
                    await validateLocation(clientData.expedientId, clientData);
                }
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            history.push(HistoryRoutes.BEFORE_START);
        }
    }

    async function loadLayout(clientData: ClientContactServiceRequest) {
        const params: RegisterServiceValues = {
            personType: clientData.personTypeKey,
            foreign: clientData.foreign,
            rol: Roles.CLIENT,
            isMigrated: user.isMigrated,
        };
        await getLayoutData(params);
        if (user.isMigrated) {
            history.push(HistoryRoutes.PRE_SIGN_TERMS);
        } else {
            history.push(HistoryRoutes.REGISTER);
        }
    }

    async function validateClientData(username?: string) {
        setIsLoading(true);
        try {
            const response: EfeResponse<ClientContactServiceRequest> =
                await ClientService.getClientContact(username ? username : user.username);
            if (response && response.data) {
                await getExpedientStatus(response.data);
            }
        } catch (error) {
            setIsLoading(false);
            history.push(HistoryRoutes.BEFORE_START);
        }
    }

    async function getClients(page: number, perPage: number, filterValue: string) {
        setIsLoading(true);
        try {
            const response: EfeResponse<ClientResponse> = await ClientService.getUsers(
                page,
                perPage,
                filterValue
            );
            if (response && response.data && response.data.data && response.data.data.length) {
                setUsers(response.data);
            }
        } catch (error: any) {
            setUsers(undefined);
            if (error.code === 404) {
                enqueueSnackbar(Messages.USERS_NOT_FOUND, {
                    variant: SnackbarVariants.WARNING,
                });
            } else {
                enqueueSnackbar(Messages.SECURITY_USERS_ERROR, {
                    variant: SnackbarVariants.WARNING,
                });
            }
        } finally {
            setIsLoading(false);
        }
    }

    function handleChangePage(page: number) {
        setPage(page);
        getClients(page, rowsPerPage, filterValue);
    }

    function handleChangeRowsPerPage(perPage: number) {
        setPage(1);
        setRowsPerPage(perPage);
        getClients(1, perPage, filterValue);
    }

    async function handleAcceptShareLocation() {
        setOpen(!open);
        let geolocationInfo: GeolocationInfo = {
            latitude: null,
            longitude: null,
        };
        geolocationInfo = await getGeolocation();
        if (null === geolocationInfo.latitude) {
            await handleRejectShareLocation(false, '');
        } else {
            const isValid = await saveGeolocation(expedientId, geolocationInfo);
            if (clientDataValue && isValid) loadLayout(clientDataValue);
        }
    }

    async function handleRejectShareLocation(highRiskCounty: boolean, message: string) {
        setGeolocationData({latitude: null, longitude: null});
        if(highRiskCounty) {
            enqueueSnackbar(message, {
                variant: SnackbarVariants.ERROR,
            });
            console.error('High Risk location');
            await KeycloakService.logout(USER_DATA);
            USER_DATA.logout = true;
            USER_DATA.loggedIn = false;
            history.push(HistoryRoutes.LOGIN);
        } else {
            enqueueSnackbar(Messages.GET_GEOLOCATION_ERROR, {
                variant: SnackbarVariants.ERROR,
            });
            await KeycloakService.logout(USER_DATA);
            USER_DATA.logout = true;
            USER_DATA.loggedIn = false;
            history.push(HistoryRoutes.LOGIN);
        }
    }

    function handleTableAction(data: TableAction) {
        const userData = data.data;
        updateUser({
            username: userData.fullName,
            idSalesforce: userData.idSalesforce,
            email: userData.email,
            fullName: userData.fullName ? userData.fullName : '',
            isMigrated: userData.isMigrated,
            loggedIn: true,
            tokenType: USER_DATA.tokenType,
            token: USER_DATA.token,
            expires: USER_DATA.expires,
            refreshToken: USER_DATA.refreshToken,
            pendingActions: user.pendingActions,
            expirationDate: USER_DATA.expirationDate,
            logout: false,
            contractService: userData.contractService,
            isAdmin: functions.isAdmin(userData.idSalesforce),
        });
        USER_DATA.email = userData.email;
        USER_DATA.pendingActions = user.pendingActions;
        USER_DATA.idSalesforce = userData.idSalesforce;
        USER_DATA.username = userData.fullName;
        USER_DATA.fullName = userData.fullName ? userData.fullName : '';
        USER_DATA.isMigrated = userData.isMigrated;
        USER_DATA.contractService = userData.contractService;

        validateClientData(userData.fullName);
    }

    function handleTextChange(event: React.ChangeEvent<{value: unknown}>) {
        setFilterValue(`${event.target.value}`);
    }

    React.useEffect(() => {
        if (functions.isAdmin(user.idSalesforce)) {
            getClients(page, rowsPerPage, filterValue);
        } else {
            console.log('call validateClientData');
            validateClientData();
        }
    }, []);

    const ADMIN_VIEW = (
        <>
            <MainContent>
                <Box>
                    {functions.isAdmin(user.idSalesforce) && (
                        <>
                            <Grid item xs={12} key={'grid-item-search'}>
                                <Box
                                    display='flex'
                                    flexDirection='row'
                                    justifyContent='right'
                                    key={'box-search'}>
                                    <Grid item xs={6} key={'grid-item-left-search'}>
                                        <EfeFieldText
                                            fullWidth
                                            id='filterValue'
                                            name='filterValue'
                                            label=''
                                            placeholder='Nombre / Raz&oacute;n social'
                                            value={filterValue}
                                            onChange={handleTextChange}
                                        />
                                    </Grid>
                                    <Grid item key={'grid-item-right-search'}>
                                        <IconButton
                                            onClick={() => handleChangeRowsPerPage(rowsPerPage)}
                                            disabled={!filterValue}>
                                            <Search color='primary' />
                                        </IconButton>
                                    </Grid>
                                </Box>
                            </Grid>
                            <EfeTable
                                title={'Clientes'}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                data={users?.data || []}
                                totalElements={users?.pagination.totalElements || 0}
                                columns={ADMIN_USERS}
                                handleChangePage={handleChangePage}
                                handleChangeRowsPerPage={handleChangeRowsPerPage}
                                handleAction={handleTableAction}
                            />
                        </>
                    )}
                </Box>
            </MainContent>
        </>
    );

    return (
        <>
            {ADMIN_VIEW}
            {isLoading && useSpinner(isLoading)}
            {open && (
                <EfeModal
                    open={open}
                    title={'Localizaci\u00F3n'}
                    sizeOverride={30}
                    reactElement={
                        <EfeLocationRequest
                            handleAccept={handleAcceptShareLocation}
                            handleReject={() => handleRejectShareLocation(false, '')}
                            message={Messages.LOCATION_ACCESS}
                        />
                    }
                    handleAction={handleRejectShareLocation}
                />
            )}
            {showView && (
                <MainContent>
                    <Box m={4} mb={2}>
                        <Typography component={'span'} variant='h3'>
                            {Messages.WARNING}
                        </Typography>
                    </Box>
                    <Box m={4} mb={8}>
                        <Typography component={'span'} variant='h4'>
                            {Messages.ON_VALIDATION}
                        </Typography>
                    </Box>
                    <Box m={4} mb={8}>
                        <Typography variant='h5' gutterBottom component={'span'}>
                            {Messages.CLIENT_STATUS_ACKNOWLEDGMENT}
                        </Typography>
                    </Box>
                    <AppBar position='fixed' color='primary' className={classes.appBar}>
                        <BottomContent>
                            <Grid container direction='row' justifyContent='flex-start'>
                                <Grid item xs={12}>
                                    <Box
                                        display='flex'
                                        flexDirection='row'
                                        justifyContent='center'
                                        m={2}>
                                        <Grid item xs={12}>
                                            <Typography
                                                variant='h5'
                                                gutterBottom
                                                component={'span'}>
                                                {Messages.EFECTIFINTECH}
                                            </Typography>
                                        </Grid>
                                    </Box>
                                </Grid>
                            </Grid>
                        </BottomContent>
                    </AppBar>
                </MainContent>
            )}
        </>
    );
}

export default ClientStatus;
